import {Component, inject, OnDestroy, OnInit} from '@angular/core';
import {OperationTarget} from "../../core/definitions/operation-target.enum";
import {OperationService} from "../../operations/operation.service";
import {OperationContainer} from "../../core/definitions/operation-container";
import {OperationDef} from "../../core/definitions/operation-def";
import {OperationDialogService} from "../../operations/operation-dialog.service";
import {ObjectTypes} from "../../core/definitions/object-types";
import {SuperObjectModel} from "../../core/definitions/super-object-model";
import {UploadService} from "../../core/media-upload/upload.service";
import {UploadItem, UploadResult} from "../../core/media-upload/upload.types";
import {UiToolsService} from "../../core/ui-tools.service";
import {PrimusRouterService} from "../../core/primus-router.service";
import {CmsApiService} from "../../core/cms-api.service";
import {TranslateService} from "@ngx-translate/core";

@Component({
    selector: 'app-admin-mass-media-import',
    templateUrl: './admin-mass-media-import.component.html',
    styleUrls: ['./admin-mass-media-import.component.scss'],
    standalone: false
})
export class AdminMassMediaImportComponent implements OnInit, OnDestroy {
  // Injections
  private readonly cms: CmsApiService = inject(CmsApiService);
  private readonly operationDialogService: OperationDialogService = inject(OperationDialogService);
  private readonly operationService: OperationService = inject(OperationService);
  private readonly primusRouter: PrimusRouterService = inject(PrimusRouterService);
  private readonly translate: TranslateService = inject(TranslateService);
  private readonly uiTools: UiToolsService = inject(UiToolsService);
  private readonly uploadService: UploadService = inject(UploadService);

  preOperationContainer: OperationContainer;
  mediaFolder: SuperObjectModel;
  computer: boolean;
  uploadStatus: string;
  uploadStatusText: string;
  uploadQueue: UploadItem[] = [];
  displayedColumns: string[] = ['fileName', 'ok', 'statusText', 'uploadStatus'];
  importButtonDisabled = false;
  preOpButtonsDisabled = false;
  checkPublish = false;
  uploadAsAttachments = false;
  parent: any;
  uploadStatusCheckRunning = false;
  private maxUploadStatusChecks = 60;
  private uploadStatusTimeoutId: any;

  ngOnInit(): void {
    this.parent = {
      adminType: 'admin',
      hasSubMenus: false,
      currentState: this.primusRouter.currentState()
    };
    this.computer = this.uiTools.getClientType().isComputer;
    const lastUploadWorkerResult = this.uploadService.getLastUploadWorkerResult();
    if (lastUploadWorkerResult) {
      this.syncUploadStatus(lastUploadWorkerResult);
      this.mediaFolder = this.uploadService.getLastMediaFolder();
    }
    this.createOperationContainers().then();
    if (this.uploadService.hasActiveUploadWorker()) {
      this.importButtonDisabled = true;
      this.preOpButtonsDisabled = true;
      this.uploadService.setUploadWorkerCallback((data: UploadResult) => {
        this.workerMessageCallback(data);
      });
    }
  }

  ngOnDestroy() {
    if (this.uploadStatusTimeoutId) {
      clearTimeout(this.uploadStatusTimeoutId);
    }
  }

  async openPreProdOperation(operation: OperationDef) {
    this.operationService.setCurrentOperation(this.preOperationContainer, operation);
    this.preOperationContainer.currentOperation.$$showOperationView = true;
    const operationResult = await this.operationDialogService.openOperationDialog(this.preOperationContainer);
    let folderObject: SuperObjectModel;
    if (operationResult.resultObject?.object_type === ObjectTypes.FOLDER) {
      folderObject = operationResult.resultObject;
    } else if (operationResult.resultObject?.object_type === 'SelectMediaFolderOperationModel'
      && operationResult.resultObject.folder_id) {
      folderObject = await this.cms.getArtifact({
        artifact_id: operationResult.resultObject.folder_id, suppressErrHandler: true});
    }
    if (folderObject) {
      this.mediaFolder = folderObject;
      this.importButtonDisabled = false;
      this.resetUploadState();
    }
  }

  selectFiles(fileInput: HTMLInputElement) {
    fileInput.click()
    this.resetUploadState();
  }

  onFileSelect(event: any) {
    const input = event.target as HTMLInputElement;
    if (!input.files || input.files.length === 0) {
      return;
    }

    this.preOpButtonsDisabled = true;
    this.importButtonDisabled = true;

    this.uploadService.uploadFilesWithWorker(
      input.files,
      this.mediaFolder,
      {
        checkPublish: this.checkPublish,
        uploadAsAttachments: this.uploadAsAttachments,
      },
      (data: UploadResult) => {
        this.workerMessageCallback(data);
      }
    );
  }

  getCompletedUploadsCount(): number {
    if (!this.uploadQueue.length) return 0;
    return this.uploadQueue.filter(item => item.state === 'success' || item.state === 'failed').length
  }

  getUploadProgressPercent(): number {
    if (!this.uploadQueue.length) return 0;
    const completed = this.getCompletedUploadsCount();
    return Math.round((completed / this.uploadQueue.length) * 100);
  }

  private workerMessageCallback(data: UploadResult) {
    this.syncUploadStatus(data);

    if (data.uploadStatus === 'uploadFinished' || data.uploadStatus === 'failed') {
      this.preOpButtonsDisabled = false;
      this.importButtonDisabled = false;
    }

    if (this.queuedItemsToCheck().length && !this.uploadStatusCheckRunning) {
      this.checkUploadStatus().then();
    }
  }

  private async createOperationContainers() {
    this.preOperationContainer = await this.operationService.createOperationContainer(
      OperationTarget.OPERATION_TARGET_MASS_MEDIA_IMPORT_PRE_OPS_VIEW);
    await this.operationService.setOperations(this.preOperationContainer);
  }

  private syncUploadStatus(lastUploadWorkerResult: UploadResult) {
    this.uploadStatus = lastUploadWorkerResult.uploadStatus;
    this.uploadStatusText = lastUploadWorkerResult.statusText;

    // Add new images to the upload queue
    for (const item of lastUploadWorkerResult.items) {
      const existingItem = this.uploadQueue.find((itm) => itm.id === item.id);
      if (existingItem) {
        // Sync existing image status with data from worker
        existingItem.artifactId = item.artifactId;
        existingItem.state = item.state;
        existingItem.status = item.status;
        existingItem.statusText = item.statusText;
      } else {
        // Add new image to the queue
        item.uploadStatus = null;
        item.uploadStatusText = '';
        item.uploadStatusCounter = 0;
        this.uploadQueue.push(item);
      }
    }
  }

  private async checkUploadStatus() {
    this.uploadStatusCheckRunning = true;

    for (const item of this.queuedItemsToCheck().slice(0, 25)) { // Be a bit nicer
      if (!item.uploadStatusText) {
        item.uploadStatusText = this.translate.instant('TRANS__UPLOAD_STATUS__CHECKING_UPLOAD_STATUS')
      }

      try {
        item.uploadStatus = await this.cms.getUploadStatus({artifact_id: item.artifactId});
        item.uploadStatusText = this.getUploadStatusText(item.uploadStatus);
      } catch (e) {
        // Give up checking if the API call itself fails
        console.error('Error checking upload status', e);
        item.uploadStatus = 'error';
      }

      if (item.uploadStatus === 'error' || item.uploadStatusCounter >= this.maxUploadStatusChecks) {
        item.uploadStatusText = this.translate.instant('TRANS__UPLOAD_STATUS__STOPPED_CHECKING')
      }

      item.uploadStatusCounter++;
    }

    if (this.queuedItemsToCheck().length > 0) {
      this.redoUploadStatusCheck();
    } else {
      this.uploadStatusCheckRunning = false;
    }
  }

  private queuedItemsToCheck() {
    return this.uploadQueue.filter((image) =>
      image.state === 'success' &&
      (image.uploadStatus === null || (image.uploadStatus !== 'done' && image.uploadStatus !== 'error'
        && image.uploadStatusCounter < this.maxUploadStatusChecks))
    );
  }

  private redoUploadStatusCheck() {
    if (this.uploadStatusTimeoutId) {
      clearTimeout(this.uploadStatusTimeoutId);
    }
    this.uploadStatusTimeoutId = setTimeout(() => {
      this.checkUploadStatus().then();
    }, 1000);
  }

  private getUploadStatusText(status: string) {
    let res = status;
    const statusTransKey = {
      'init': 'TRANS__UPLOAD_STATUS__INIT',
      'convert': 'TRANS__UPLOAD_STATUS__CONVERTING',
      'done': 'TRANS__UPLOAD_STATUS__DONE',
    }[status];
    if (statusTransKey) {
      res = this.translate.instant(statusTransKey);
    }
    return res;
  }

  private resetUploadState() {
    this.uploadStatus = null;
    this.uploadStatusText = null;
    this.uploadQueue = [];
  }
}
